/**
 * PURPOSE: Mostrar actividad reciente de programación, entrenamientos y uniformes.
 * RESPONSIBILITIES: Filtrar, ordenar, agrupar operaciones y marcar notificaciones como revisadas.
 * COLLABORATORS: Navbar.jsx y auditNotificationService.js.
 * ROUTE: winscore/src/components/global/AuditNotificationPanel/AuditNotificationPanel.jsx
 */
import React, { useEffect, useMemo, useState } from "react";
import { FaChevronDown, FaChevronUp, FaFilter, FaTimes } from "react-icons/fa";
import {
  getAuditFilterOptions,
  getAuditNotifications,
  markAuditNotificationsRead,
} from "../../../services/auditNotificationService.js";

import { AuditNotificationDetails } from "./AuditNotificationDetails.jsx";
import { ACTION_LABELS, MODULE_LABELS, getAuditContextTitle, getAuditDisplaySummary, getAuditScopeLabel } from "./auditDisplay.utils.js";

const dateKey = (value) => new Date(value).toLocaleDateString("es-PE", {
  timeZone: "America/Lima",
  year: "numeric",
  month: "long",
  day: "numeric",
});


const operationDateTime = (value) => new Date(value).toLocaleString("es-PE", {
  timeZone: "America/Lima",
  dateStyle: "medium",
  timeStyle: "short",
});

const groupByOperation = (items) => {
  const map = new Map();
  items.forEach((item) => {
    const key = `${dateKey(item.createdAt)}:${item.operationId}`;
    if (!map.has(key)) map.set(key, { date: dateKey(item.createdAt), operationId: item.operationId, items: [] });
    map.get(key).items.push(item);
  });
  return Array.from(map.values());
};

export function AuditNotificationPanel({ open, onClose, onRead }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ events: [], sports: [], users: [] });
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    idevent: "",
    idsport: "",
    actorUserId: "",
    entityType: "",
    action: "",
    dateFrom: "",
    dateTo: "",
    order: "desc",
    unreadOnly: false,
  });

  const load = async ({ append = false } = {}) => {
    setLoading(true);
    try {
      const payload = await getAuditNotifications({
        ...filters,
        limit: 40,
        ...(append && nextCursor ? { beforeAuditId: nextCursor } : {}),
      });
      const pageItems = payload.items || [];
      setItems((current) => (append ? [...current, ...pageItems] : pageItems));
      setHasMore(Boolean(payload.hasMore));
      setNextCursor(payload.nextCursor || null);
      const maxId = Math.max(0, ...pageItems.map((item) => Number(item.id) || 0));
      if (maxId) await markAuditNotificationsRead(maxId);
      onRead?.();
    } catch (error) {
      console.error("No se pudo cargar la actividad reciente", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    getAuditFilterOptions()
      .then((payload) => setFilterOptions({
        events: payload.events || [],
        sports: payload.sports || [],
        users: payload.users || [],
      }))
      .catch((error) => console.error("No se pudieron cargar los filtros de auditoría", error));
    load({ append: false });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const listener = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [open, onClose]);

  const groups = useMemo(() => groupByOperation(items), [items]);
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] bg-slate-950/40 backdrop-blur-sm" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <aside className="ml-auto flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-gray-900">
        <header className="flex items-start justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600">Winscore</p>
            <h2 className="text-2xl font-black text-gray-900 dark:text-white">Actividad reciente</h2>
            <p className="text-sm text-gray-500">Cambios registrados en las compañías 1 y 2.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full bg-gray-100 p-3 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200"><FaTimes /></button>
        </header>

        <div className="border-b border-gray-200 p-4 dark:border-gray-700">
          <div className="flex gap-2">
            <input value={filters.search} onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))} placeholder="Buscar usuario, evento o cambio" className="min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />
            <button type="button" onClick={() => setFiltersOpen((value) => !value)} className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 text-sm font-bold"><FaFilter /> Filtros</button>
            <button type="button" onClick={() => load({ append: false })} className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700">Aplicar</button>
          </div>
          {filtersOpen ? (
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <select value={filters.idevent} onChange={(event) => setFilters((current) => ({ ...current, idevent: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white">
                <option value="">Todos los eventos</option>
                {filterOptions.events.map((event) => <option key={event.id} value={event.id}>{event.name}</option>)}
              </select>
              <select value={filters.idsport} onChange={(event) => setFilters((current) => ({ ...current, idsport: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white">
                <option value="">Todas las disciplinas</option>
                {filterOptions.sports.map((sport) => <option key={sport.id} value={sport.id}>{sport.name}</option>)}
              </select>
              <select value={filters.actorUserId} onChange={(event) => setFilters((current) => ({ ...current, actorUserId: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white">
                <option value="">Todos los usuarios</option>
                {filterOptions.users.map((user) => <option key={user.id} value={user.id}>{user.username}</option>)}
              </select>
              <select value={filters.entityType} onChange={(event) => setFilters((current) => ({ ...current, entityType: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white">
                <option value="">Todos los módulos</option>
                {Object.entries(MODULE_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <select value={filters.action} onChange={(event) => setFilters((current) => ({ ...current, action: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white">
                <option value="">Todas las acciones</option>
                {Object.entries(ACTION_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
              <select value={filters.order} onChange={(event) => setFilters((current) => ({ ...current, order: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white"><option value="desc">Más recientes</option><option value="asc">Más antiguos</option></select>
              <input type="date" value={filters.dateFrom} onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />
              <input type="date" value={filters.dateTo} onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:bg-gray-800 dark:text-white" />
              <label className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold"><input type="checkbox" checked={filters.unreadOnly} onChange={(event) => setFilters((current) => ({ ...current, unreadOnly: event.target.checked }))} /> Solo sin revisar</label>
            </div>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? <div className="rounded-2xl bg-gray-50 p-6 text-center font-semibold text-gray-500">Cargando actividad...</div> : null}
          {!loading && !groups.length ? <div className="rounded-2xl border border-dashed border-gray-300 p-8 text-center font-semibold text-gray-500">No hay cambios para mostrar.</div> : null}
          <div className="space-y-4">
            {groups.map((group) => {
              const key = `${group.date}:${group.operationId}`;
              const first = group.items[0];
              const isExpanded = Boolean(expanded[key]);
              return (
                <section key={key} className="overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
                  <button type="button" onClick={() => setExpanded((current) => ({ ...current, [key]: !current[key] }))} className="flex w-full items-start justify-between gap-3 bg-gray-50 px-4 py-3 text-left dark:bg-gray-800">
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-wide text-gray-400">{operationDateTime(first.createdAt)} · {MODULE_LABELS[first.entityType] || first.entityType}</p>
                      <p className="mt-1 truncate text-xs font-black uppercase tracking-wide text-blue-600">{first.actor?.username || "Usuario"}</p>
                      <p className="mt-1 font-black text-gray-900 dark:text-white">{getAuditScopeLabel(first)} {first.sport?.name ? `· ${first.sport.name}` : ""}</p>
                      <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">{group.items.length > 1 ? `${group.items.length} cambios agrupados · ${getAuditContextTitle(first)}` : getAuditContextTitle(first)}</p>
                    </div>
                    {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                  {isExpanded ? (
                    <div className="divide-y divide-gray-100 dark:divide-gray-800">
                      {group.items.map((item) => (
                        <article key={item.id} className="p-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="font-black text-gray-900 dark:text-white">{getAuditDisplaySummary(item)}</p>
                            <time className="text-xs font-bold text-gray-400">{new Date(item.createdAt).toLocaleString("es-PE", { timeZone: "America/Lima", dateStyle: "short", timeStyle: "short" })}</time>
                          </div>
                          <p className="mt-1 text-xs font-bold uppercase tracking-wide text-blue-600">{item.actor?.username} · {ACTION_LABELS[item.action] || item.action}</p>
                          <AuditNotificationDetails item={item} />
                        </article>
                      ))}
                    </div>
                  ) : null}
                </section>
              );
            })}
          </div>
          {hasMore ? (
            <button
              type="button"
              disabled={loading}
              onClick={() => load({ append: true })}
              className="mt-4 w-full rounded-xl border border-gray-300 px-4 py-3 text-sm font-black text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              {loading ? "Cargando..." : "Cargar más cambios"}
            </button>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
